import {
  languagesSelector,
  lobbyConfigSelector,
  skinColorsSelector,
} from '@store/selectors/config';
import { userSelector } from '@store/selectors/user';
import setSkin from '@utils/setSkin';
import humps from 'humps';
import { combineEpics, ofType } from 'redux-observable';
import { of, from, EMPTY } from 'rxjs';
import {
  switchMap,
  catchError,
  mergeMap,
  ignoreElements,
  tap,
} from 'rxjs/operators';

import {
  mapPropNames, handleError, ss, LOCATION_ORIGIN_QUERY_PARAM, getEncodedLocationOrigin,
} from '@ezugi/utils';

import { app } from '../../app';
import { categories } from '../../categories';
import { config } from '../../config';
import { externalGameTypes } from '../../externalGameTypes';
import { locale } from '../../locale';
import { socket } from '../../socket';
import { user } from '../../user';
import { authenticate, changeLanguage } from './utils';

export const camelize = mapPropNames(humps.camelize);

const configEpic = (action$) => action$.pipe(
  ofType(config.request),
  switchMap(({ payload }) => {
    const urlParams = new URLSearchParams(window?.location?.search);
    const languageFromURLParams = urlParams.get('language');

    const params = {
      ...payload,
      game_id: -99,
      table_id: -99,
      [LOCATION_ORIGIN_QUERY_PARAM]: getEncodedLocationOrigin('base64'),
    };

    if (languageFromURLParams) {
      params.language = languageFromURLParams;
    }

    if (!payload.identifier && payload.token && payload.operatorId) {
      params.identifier = String(payload.operatorId).concat(payload.token);
    }

    return from(authenticate(params)).pipe(
      mergeMap((data) => {
        const { operatorId, token } = data.result.authentication_params || {
          operatorId: urlParams.get('operatorId'),
          token: urlParams.get('token'),
        };

        // Intentional mutation - Add the category name to `data.result.groups`
        Object.values(data.result.config?.filters?.game_groups).forEach(
          (group) => {
            if (
              Object.prototype.hasOwnProperty.call(
                data.result.groups,
                group.id
              )
            ) {
              data.result.groups[group.id].name = group.name;
            }
          }
        );

        // stored in ss for situations in which the user no longer has a valid session (home config unavailable)
        // so that it can be used for redirecting to login page in GameError component
        if (data.result.config?.home?.home_url) {
          ss.put('home_url', data.result.config?.home?.home_url);
        }

        return of(
          user.set({
            favorite: (data.result.favorite ?? []).map(Number),
            authParams: data.result.authentication_params,
            operatorId,
            token,
            identifier: String(operatorId).concat(token),
            ...data.result.user_data,
          }),
          config.set({
            ...data.result.config,
            excludedTables: data.result.exclude_tables,
            excludedGames: data.result.exclude_games,
            gameTypes: data.result.game_types,
            language: data.result.config?.general?.available_languages.find(
              (lang) => lang.code === data.result.language
            ),
            definitions: data.result.definitions,
            amount_display: camelize(data.result.config.amount_display),
          }),
          externalGameTypes.set(
            Object.keys(data.result.game_types_from_providers).map(
              (gameTypeIdString) => Number(gameTypeIdString)
            )
          ),
          locale.set(data.result.locale),
          categories.set(Object.values(data.result.groups)),
          socket.request(),
          config.success()
        );
      })
    );
  }),
  catchError((error) => handleError({
    error,
    failAction: app.update,
    options: {
      httpErrorHandler: (err) => (err.response?.locale
        ? of(
          locale.set(err.response?.locale),
          config.update({ ...err.response?.initialConfig }),
          config.failure()
        )
        : EMPTY),
      clientErrorHandler: () => of(config.failure()),
      payloadKey: 'error',
    },
  }))
);

const updateSkinEpic = (action$, state$) => action$.pipe(
  ofType(config.set, config.update),
  tap(() => {
    setSkin({
      colors: skinColorsSelector(state$.value),
      table: lobbyConfigSelector(state$.value),
    });
  }),
  ignoreElements()
);

const changeLanguageEpic = (action$, state$) => action$.pipe(
  ofType(config.changeLanguage),
  tap(({ payload }) => {
    document.documentElement.lang = payload.code;
  }),
  switchMap(({ payload: { code } }) => {
    const { operatorId, identifier } = userSelector(state$.value);

    const params = {
      language: code,
      operator_id: operatorId,
      table_id: -99,
      game_id: -99,
      identifier,
    };

    return from(changeLanguage(params));
  }),
  mergeMap(({ result, code }) => of(
    locale.set(result.locale),
    config.update({
      language: languagesSelector(state$.value)?.find(
        (lang) => lang.code === code
      ),
    })
  )),
  catchError((error) => handleError({
    error,
    failAction: config.failure,
  }))
);

export default combineEpics(configEpic, updateSkinEpic, changeLanguageEpic);
